import { Application } from './core/app';
import { HOST } from './constants/api';
import axios from 'axios';
import { clearAuthentikToken, refreshAuthentikToken } from './components/Login/authentik';
import { showToast } from './toast';

/**
 * We'll load the axios HTTP library which allows us to easily issue requests
 * to our Laravel back-end. This library automatically handles sending the
 * CSRF token as a header based on the value of the "XSRF" token cookie.
 */

window.axios = axios;



/**
 * Next we will register the Base URL with Axios so that all outgoing 
 * HTTP requests automatically have it attached. This is just
 * a simple convenience so we do not have to attach the base url in each 
 * request.
 */

axios.defaults.baseURL = HOST;

/**
 * Next we will register the application access token on headers so that all outgoing 
 * HTTP requests automatically have it attached. This is just
 * a simple convenience so we do not have to attach the access token in each 
 * request.
 */
// Token headers are now managed by AuthProvider to ensure proper timing
axios.defaults.timeout = 30000;

// Authentik refresh state - prevent concurrent refresh attempts
let authentikRefreshing = false;
let authentikRefreshPromise = null;

/**
 * Show/hide a full-screen loading spinner during token refresh.
 */
const showRefreshSpinner = () => {
  let el = document.getElementById('authentik-refresh-spinner');
  if (!el) {
    el = document.createElement('div');
    el.id = 'authentik-refresh-spinner';
    el.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;pointer-events:none;';
    el.innerHTML = '<div style="background:rgba(33,33,33,0.95);padding:24px 32px;border-radius:12px;display:flex;flex-direction:column;align-items:center;gap:12px;"><div style="width:40px;height:40px;border:3px solid rgba(255,255,255,0.2);border-top-color:#4caf50;border-radius:50%;animation:spin 0.8s linear infinite;"></div><span style="color:#fff;font-size:14px;font-family:sans-serif;">Refreshing session…</span></div><style>@keyframes spin{to{transform:rotate(360deg)}}</style>';
    document.body.appendChild(el);
  }
};
const hideRefreshSpinner = () => {
  const el = document.getElementById('authentik-refresh-spinner');
  if (el) el.remove();
};

/**
 * Get the Authentik auth token from localStorage.
 */
const getAuthToken = () => {
  const token = Application.getAccessToken();
  if (token) return token;

  // Fallback: check localStorage directly
  return localStorage.getItem('authentik_token') || null;
};

axios.interceptors.request.use(async (config) => {
  const token = await getAuthToken();

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
    config.headers.accessToken = token;
  }

  return config;
});

/**
 * We configure an axi interceptor to capture errors in which we wish to perform the same logic.
 * In this way we avoid doing the same in each application.
 */
axios.interceptors.response.use(undefined, async function (error) {

  if (!error.response && !error.code) { // Network error
    showToast('Network error. Please check your connection.', { severity: 'error' });
  } else if (error.code === 'ECONNABORTED') { // Timeout request
    showToast('Request timed out. Please try again.', { severity: 'error' });
  } else {
    /**
     * Handle Laravel validation errors (422)
     */
    if (error.response.status === 422 && error.response.data?.errors) {
      const messages = Object.values(error.response.data.errors).flat();
      if (messages.length > 0) {
        showToast(messages[0], { severity: 'error' });
      }
      // Attach error info for components that want to handle it programmatically
      error.validationErrors = error.response.data.errors;
      error.validationMessage = messages.join(' ');
    }
    
    /**
     * Error 400, user not authorized to permorm this request.
     * Typically when the access token is invalid or has not been sent in the headers.
     */
    if(error.response.status === 400) {
    //   store.dispatch(userLogOut());
    }
    if (error.response.status === 401) {
        const originalRequest = error.config || {};
        const provider = Application.getAuthProvider();

        // Authentik: try to refresh token and retry
        if (provider === 'authentik' && !originalRequest._retry) {
          // If already refreshing, queue this request to retry after refresh completes
          if (authentikRefreshing && authentikRefreshPromise) {
            return authentikRefreshPromise.then(() => {
              originalRequest.headers = originalRequest.headers || {};
              const t = Application.getAccessToken();
              if (t) {
                originalRequest.headers.Authorization = `Bearer ${t}`;
                originalRequest.headers.accessToken = t;
                return axios(originalRequest);
              }
              return Promise.reject(error);
            });
          }

          authentikRefreshing = true;
          showRefreshSpinner();

          const refreshPromise = (async () => {
            try {
              const newToken = await refreshAuthentikToken();
              hideRefreshSpinner();
              if (newToken) {
                // Refresh succeeded - all queued requests will pick up the new token
                authentikRefreshing = false;
                authentikRefreshPromise = null;
                return newToken;
              }
              // Refresh failed — log out
              clearAuthentikToken();
              Application.logout();
              delete window.axios.defaults.headers.common['Authorization'];
              delete window.axios.defaults.headers.common['accessToken'];
              authentikRefreshing = false;
              authentikRefreshPromise = null;
              window.dispatchEvent(new CustomEvent('authstatechange', { detail: 'logout' }));
              showToast('Session expired. Please sign in again.', { severity: 'warning' });
              window.location.href = '/login';
              return null;
            } catch (refreshErr) {
              hideRefreshSpinner();
              clearAuthentikToken();
              Application.logout();
              delete window.axios.defaults.headers.common['Authorization'];
              delete window.axios.defaults.headers.common['accessToken'];
              authentikRefreshing = false;
              authentikRefreshPromise = null;
              window.dispatchEvent(new CustomEvent('authstatechange', { detail: 'logout' }));
              showToast('Session expired. Please sign in again.', { severity: 'warning' });
              window.location.href = '/login';
              return null;
            }
          })();

          authentikRefreshPromise = refreshPromise;

          const newToken = await refreshPromise;
          if (!newToken) return Promise.reject(error);

          // Retry the original request with the new token
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          originalRequest.headers.accessToken = newToken;
          return axios(originalRequest);
        }

        // Check for expired Authentik token even when provider isn't set (race condition / stale state)
        if (provider !== 'authentik' && sessionStorage.getItem('authentik_token') && !originalRequest._retry) {
          // This is an expired Authentik session with stale state — clear and redirect
          clearAuthentikToken();
          Application.logout();
          delete window.axios.defaults.headers.common['Authorization'];
          delete window.axios.defaults.headers.common['accessToken'];
          window.dispatchEvent(new CustomEvent('authstatechange', { detail: 'logout' }));
          showToast('Session expired. Please sign in again.', { severity: 'warning' });
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // Firebase 401 retry removed — Authentik-only auth

        // Generic unauthorized message (only shown if not already handled above)
        showToast(error.response.data?.message || 'This request was not authorized.', {
          severity: 'error',
        });
    }

    /**
     * Handle forbidden errors
     */
    if (error.response.status === 403) {
      showToast(error.response.data?.message || 'You do not have permission to perform this action.', {
        severity: 'error',
      });
    }

    /**
     * Handle server errors
     */
    if (error.response.status >= 500) {
      showToast('A server error occurred. Please try again later.', { severity: 'error' });
    }

    /**
     * This is the error returned by the api for personalized messages, 
     * maybe some disabled functionality.
     */
    if (error.response.status === 580) {
    //   store.dispatch(showMessageBar({
    //     status: 'error',
    //     text: error.response.data.message
    //   }));
    }
  }

  return Promise.reject(error);
});